import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { AppShell } from '@/components/shared/AppShell'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ArrowLeft, Save, Eye, Edit3 } from 'lucide-react'
import { CoverImageUploader } from '@/components/admin/CoverImageUploader'

interface Props {
  categories: string[]
}

const CATEGORY_LABELS: Record<string, string> = {
  announcement:     'Announcements',
  exam_guide:       'Exam Guide',
  preparation_tips: 'Prep Tips',
  general:          'General',
}

export default function BlogPostsNew({ categories }: Props) {
  const [title, setTitle] = useState('')
  const [slug, setSlug] = useState('')
  const [excerpt, setExcerpt] = useState('')
  const [content, setContent] = useState('')
  const [coverImageUrl, setCoverImageUrl] = useState('')
  const [authorName, setAuthorName] = useState('PrepKiya Team')
  const [category, setCategory] = useState('general')
  const [examTags, setExamTags] = useState('')
  const [seoTitle, setSeoTitle] = useState('')
  const [seoDescription, setSeoDescription] = useState('')
  const [seoKeywords, setSeoKeywords] = useState('')
  const [publishImmediately, setPublishImmediately] = useState(false)
  const [previewTab, setPreviewTab] = useState<'edit' | 'preview'>('edit')
  const [submitting, setSubmitting] = useState(false)

  const generateSlug = (value: string) => {
    return value.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').trim()
  }

  const handleTitleChange = (value: string) => {
    setTitle(value)
    if (!slug || slug === generateSlug(title)) {
      setSlug(generateSlug(value))
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim() || !excerpt.trim() || !content.trim()) {
      alert('Title, Excerpt, and Content are required.')
      return
    }
    setSubmitting(true)
    router.post(
      '/admin/blog_posts',
      {
        blog_post: {
          title: title.trim(),
          slug: slug.trim(),
          excerpt: excerpt.trim(),
          content: content.trim(),
          cover_image_url: coverImageUrl.trim(),
          author_name: authorName.trim(),
          category,
          exam_tags: examTags.split(',').map(t => t.trim()).filter(Boolean),
          seo_title: seoTitle.trim(),
          seo_description: seoDescription.trim(),
          seo_keywords: seoKeywords.trim(),
        },
        publish_immediately: publishImmediately ? '1' : '0',
      },
      { onFinish: () => setSubmitting(false) }
    )
  }

  return (
    <AppShell>
      <div className="space-y-6 max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-2 border-b border-border">
          <div className="flex items-center gap-3">
            <Button asChild variant="ghost" size="sm" className="h-9 w-9 p-0">
              <Link href="/admin/blog_posts"><ArrowLeft className="w-4 h-4" /></Link>
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-foreground">New Blog Post</h1>
              <p className="text-xs text-muted-foreground mt-0.5">Create a new article for the public blog.</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">

          {/* Core Fields */}
          <Card className="border-border bg-card shadow-xs">
            <CardHeader className="px-6 py-4 border-b border-border">
              <CardTitle className="text-base font-bold text-foreground">Post Details</CardTitle>
              <CardDescription className="text-xs text-muted-foreground">Title, category, author, and cover image.</CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <div className="sm:col-span-2 space-y-2">
                  <Label htmlFor="title" className="text-xs font-semibold text-foreground">Title *</Label>
                  <Input id="title" value={title} onChange={(e) => handleTitleChange(e.target.value)} placeholder="e.g. How to Crack SBI Clerk 2025" required className="text-sm" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category" className="text-xs font-semibold text-foreground">Category *</Label>
                  <select
                    id="category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                  >
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{CATEGORY_LABELS[cat] || cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="slug" className="text-xs font-semibold text-foreground">URL Slug *</Label>
                  <Input id="slug" value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="e.g. sbi-clerk-2025-guide" required className="font-mono text-sm" />
                  <p className="text-[11px] text-muted-foreground">Auto-generated from title. Only lowercase, numbers, hyphens.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="author_name" className="text-xs font-semibold text-foreground">Author Name</Label>
                  <Input id="author_name" value={authorName} onChange={(e) => setAuthorName(e.target.value)} placeholder="PrepKiya Team" className="text-sm" />
                </div>
              </div>

              <CoverImageUploader
                value={coverImageUrl}
                onChange={setCoverImageUrl}
                disabled={submitting}
              />

              <div className="space-y-2">
                <Label htmlFor="exam_tags" className="text-xs font-semibold text-foreground">Exam Tags</Label>
                <Input id="exam_tags" value={examTags} onChange={(e) => setExamTags(e.target.value)} placeholder="GATE, SBI Clerk, NISM (comma-separated)" className="text-sm" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="excerpt" className="text-xs font-semibold text-foreground">Excerpt * <span className="font-normal text-muted-foreground">(max 300 chars)</span></Label>
                <textarea
                  id="excerpt"
                  value={excerpt}
                  onChange={(e) => setExcerpt(e.target.value)}
                  rows={3}
                  maxLength={300}
                  required
                  placeholder="A short 1-2 sentence summary shown on listing cards and search results..."
                  className="w-full text-sm p-3 rounded-xl border border-border bg-background text-foreground leading-relaxed focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                />
                <p className="text-[11px] text-muted-foreground">{excerpt.length}/300 characters</p>
              </div>
            </CardContent>
          </Card>

          {/* Content Editor */}
          <Card className="border-border bg-card shadow-xs">
            <CardHeader className="px-6 py-4 border-b border-border flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base font-bold text-foreground">Content *</CardTitle>
                <CardDescription className="text-xs text-muted-foreground">Full article body. HTML is supported.</CardDescription>
              </div>
              <div className="flex items-center gap-1 bg-muted p-1 rounded-lg border border-border">
                <button type="button" onClick={() => setPreviewTab('edit')} className={`flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-md transition-colors ${previewTab === 'edit' ? 'bg-card text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground'}`}>
                  <Edit3 className="w-3.5 h-3.5" /><span>Editor</span>
                </button>
                <button type="button" onClick={() => setPreviewTab('preview')} className={`flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-md transition-colors ${previewTab === 'preview' ? 'bg-card text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground'}`}>
                  <Eye className="w-3.5 h-3.5" /><span>Preview</span>
                </button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {previewTab === 'edit' ? (
                <textarea
                  id="content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={22}
                  required
                  placeholder="Write your article here. HTML tags like <h2>, <p>, <ul>, <strong> are supported..."
                  className="w-full font-mono text-xs sm:text-sm p-4 rounded-xl border border-border bg-background text-foreground leading-relaxed focus:outline-none focus:ring-1 focus:ring-primary resize-y"
                />
              ) : (
                <div
                  className="p-6 rounded-xl border border-border bg-background min-h-[400px] max-h-[500px] overflow-y-auto text-foreground leading-relaxed prose-like text-sm"
                  dangerouslySetInnerHTML={{ __html: content || '<p class="text-muted-foreground">Nothing to preview.</p>' }}
                />
              )}
            </CardContent>
          </Card>

          {/* SEO Fields */}
          <Card className="border-border bg-card shadow-xs">
            <CardHeader className="px-6 py-4 border-b border-border">
              <CardTitle className="text-base font-bold text-foreground">SEO Settings <span className="text-xs font-normal text-muted-foreground">(optional)</span></CardTitle>
              <CardDescription className="text-xs text-muted-foreground">Override title tag, meta description, and keywords for search engines.</CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="seo_title" className="text-xs font-semibold text-foreground">SEO Title</Label>
                <Input id="seo_title" value={seoTitle} onChange={(e) => setSeoTitle(e.target.value)} placeholder="Defaults to post title if blank" className="text-sm" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="seo_description" className="text-xs font-semibold text-foreground">Meta Description</Label>
                <Input id="seo_description" value={seoDescription} onChange={(e) => setSeoDescription(e.target.value)} placeholder="Defaults to excerpt if blank" className="text-sm" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="seo_keywords" className="text-xs font-semibold text-foreground">Keywords</Label>
                <Input id="seo_keywords" value={seoKeywords} onChange={(e) => setSeoKeywords(e.target.value)} placeholder="e.g. GATE preparation, SBI Clerk tips" className="text-sm" />
              </div>
            </CardContent>
          </Card>

          {/* Publish Footer */}
          <Card className="border-border bg-card shadow-xs">
            <CardContent className="p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input type="checkbox" checked={publishImmediately} onChange={(e) => setPublishImmediately(e.target.checked)} className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-ring cursor-pointer" />
                <div>
                  <span className="text-sm font-semibold text-foreground block">Publish immediately</span>
                  <span className="text-xs text-muted-foreground">If unchecked, post is saved as Draft and not visible publicly.</span>
                </div>
              </label>
              <div className="flex items-center gap-3 self-end sm:self-auto">
                <Button asChild variant="outline"><Link href="/admin/blog_posts">Cancel</Link></Button>
                <Button type="submit" disabled={submitting} className="gap-2 font-semibold">
                  <Save className="w-4 h-4" />
                  {submitting ? 'Saving...' : (publishImmediately ? 'Save & Publish' : 'Save as Draft')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </div>
    </AppShell>
  )
}
